import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type { MaskingShapeObject} from '@PosterWhiteboard/classes/masking/masking-shape.class';
import {MaskingShape} from '@PosterWhiteboard/classes/masking/masking-shape.class';
import {MaskingType} from '@PosterWhiteboard/classes/masking/masking.types';
import type { MaskingTextObject} from '@PosterWhiteboard/classes/masking/masking-text.class';
import {MaskingText} from '@PosterWhiteboard/classes/masking/masking-text.class';
import type { MaskingFreehandObject} from '@PosterWhiteboard/classes/masking/masking-freehand.class';
import {MaskingFreehand} from '@PosterWhiteboard/classes/masking/masking-freehand.class';
import type {DeepPartial} from '@/global';

export type MaskingItemType = MaskingShape | MaskingText | MaskingFreehand;
export type MaskingItemObject = MaskingShapeObject | MaskingTextObject | MaskingFreehandObject;

export interface ItemMaskingObject {
  // Required and `| null`, never optional/`undefined`: "no mask" serializes as an explicit null so
  // the jsondiffpatch history round-trip carries a removal as a reversible modify. An undefined /
  // absent key is dropped by the diff, so undo could never restore or clear the mask. See toObject().
  maskingItem: MaskingItemObject | null;
}

export class ItemMasking extends CommonMethods {
  public maskingItem: MaskingItemType | null = null;

  public override copyVals(obj: DeepPartial<ItemMaskingObject> = {}): void {
    // Key absent: not part of this update — leave the current mask as-is.
    if (obj.maskingItem === undefined) {
      return;
    }
    // Explicit null: clear the mask (how a removal survives the jsondiffpatch history round-trip).
    if (obj.maskingItem === null) {
      this.maskingItem = null;
      return;
    }
    // An object: set or update the mask.
    if (obj.maskingItem.type && obj.maskingItem.type !== this.maskingItem?.type) {
      this.maskingItem = getMaskingItemForType(obj.maskingItem.type);
    }
    this.maskingItem?.copyVals(obj.maskingItem);
  }

  public async applyMaskingToImage(img: HTMLImageElement): Promise<HTMLImageElement> {
    if (!this.maskingItem) {
      return img;
    }
    return this.maskingItem.applyMaskingToImage(img);
  }

  public toObject(): ItemMaskingObject {
    // Serialize "no mask" as null, not undefined: jsondiffpatch (poster history) records an
    // undefined->object edit as an add whose reverse DELETES the key, so undo would never carry
    // the removal. null round-trips as a reversible modify, keeping the key present for copyVals.
    return {
      maskingItem: this.maskingItem !== null ? this.maskingItem.toObject() : null,
    };
  }
}

const getMaskingItemForType = (type: MaskingType): MaskingItemType => {
  switch (type) {
    case MaskingType.SHAPE:
      return new MaskingShape();
    case MaskingType.TEXT:
      return new MaskingText();
    case MaskingType.FREEHAND:
      return new MaskingFreehand();
    default:
      throw new Error(`Unhandled maskingtype ${type}`);
  }
};
